import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserRole } from '@libs/shared/api';
import { KeycloakUserRole } from '@libs/shared/interface/enum';
import { AuthUserProfile } from '@sprint-backoffice/app/core/auth/auth-user-profile';
import { KeycloakOAuthService } from '@sprint-backoffice/app/core/auth/keycloak-oauth.service';
import { injectCoreFeature } from '@sprint-backoffice/app/core/state/core.store';
import { environment } from '@sprint-backoffice/environments/environment';
import { OAuthErrorEvent } from 'angular-oauth2-oidc';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  from,
  map,
  Observable,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly core = injectCoreFeature();
  readonly oauthService = inject(KeycloakOAuthService);
  readonly router = inject(Router);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  isAuthenticated$$ = toSignal(this.isAuthenticated$);

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  isDoneLoading$$ = toSignal(this.isDoneLoading$);

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage() {
    // window.location.reload();
    this.router.navigateByUrl('/home').then();
  }

  constructor() {
    // Useful for debugging:
    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
        // Handle specific errors
        const errorReason = event.reason as any; // Cast to 'any' to access 'error' property
        if (errorReason && errorReason.error === 'interaction_required') {
          // Prompt user to re-authenticate
          this.login();
        }
        if (event.type === 'user_profile_load_error') {
          this.logout();
        }
      } else {
        console.debug('OAuthEvent Object:', event);
      }
    });

    // configure auth based on platform
    this.configureWeb();

    window.addEventListener('storage', event => {
      if (event.key !== 'auth_data_updated' && event.key !== null) {
        return;
      }
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );

      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this.oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events
      .pipe(filter(event => event.type === 'token_received'))
      .subscribe(() => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(
        filter(event =>
          ['session_terminated', 'session_error'].includes(event.type)
        )
      )
      .subscribe(event => {
        console.warn('Session issue:', event);
        // Optionally, attempt to refresh the token or prompt the user
      });

    this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');

    // Handle silent refresh errors
    this.oauthService.events
      .pipe(filter(event => event.type === 'silent_refresh_error'))
      .subscribe((event: OAuthErrorEvent) => {
        console.error('Silent refresh error:', event);
        // Decide whether to retry or take other actions
        this.navigateToLoginPage();
      });
  }

  async runInitialLoginSequence(): Promise<void> {
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(
        location.hash
          .substr(1)
          .split('&')
          .map(kvp => kvp.split('='))
      );
    }

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        this.core.fetchAuthUser();
        this.core.fetchCurrentUserRole();
        return;
      }

      if (this.oauthService.getRefreshToken()) {
        await this.refreshToken();
        return;
      }

      // Handle the case where the user is not authenticated
    } catch (e) {
      console.error('Initialization error:', e);
      // Optionally handle the error, redirect, or notify the user
    } finally {
      this.isDoneLoadingSubject$.next(true);
    }
  }

  login(targetUrl?: string): void {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initLoginFlow(targetUrl || this.router.url, {
      prompt: 'login',
    });
  }

  logout(): void {
    this.oauthService.logOut();
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  async refreshToken(): Promise<void> {
    try {
      await this.oauthService.refreshToken();
      this.core.fetchAuthUser();
      this.core.fetchCurrentUserRole();
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error; // Ensure the error is propagated
    }
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getUserProfile(): Observable<AuthUserProfile> {
    return from(
      this.oauthService.loadUserProfile() as Promise<AuthUserProfile>
    );
  }

  getCurrentUserRole(): UserRole {
    const userRoles = this.getRealmRoles();

    const roleMapping: { [key in KeycloakUserRole]: UserRole } = {
      [KeycloakUserRole.Admin]: UserRole.Admin,
      [KeycloakUserRole.SeniorManager]: UserRole.SeniorManager,
      [KeycloakUserRole.JuniorManager]: UserRole.JuniorManager,
      [KeycloakUserRole.PrepManager]: UserRole.PrepManager,
      [KeycloakUserRole.Expert]: UserRole.Expert,
      [KeycloakUserRole.PrepExpert]: UserRole.PrepExpert,
      [KeycloakUserRole.ExpertManager]: UserRole.ExpertManager,
      [KeycloakUserRole.PrepExpertManager]: UserRole.PrepExpertManager,
      [KeycloakUserRole.SilverMember]: UserRole.SilverMember,
      [KeycloakUserRole.BronzeMember]: UserRole.BronzeMember,
    };

    for (const keycloakRole of Object.keys(roleMapping) as KeycloakUserRole[]) {
      if (userRoles.includes(keycloakRole)) {
        return roleMapping[keycloakRole];
      }
    }

    return UserRole.BronzeMember;
  }

  getRealmRoles(): KeycloakUserRole[] {
    const idClaims = this.oauthService.getIdentityClaims();

    if (!idClaims) {
      console.error(
        "Couldn't get identity claims, make sure the user is signed in."
      );
      this.navigateToLoginPage();
      return [];
    }
    if (!idClaims['realm_access'] || !idClaims['realm_access']['roles']) {
      console.error(
        "Keycloak didn't provide realm_roles in the token. Have you configured the predefined mapper realm roles correctly?"
      );
      this.navigateToLoginPage();
      return [];
    }

    const realmRoles = idClaims['realm_access']['roles'];
    return realmRoles ?? [];
  }

  private configureWeb(): void {
    this.oauthService.configure(environment.authConfig.web);
  }
}
